import React, { useEffect, useMemo, useState } from "react";
import { Offcanvas, Form, Button, Alert, Spinner } from "react-bootstrap";
import ReactSelect from "react-select";
import { Icon } from "../../../utils/iconHelper";
import BatchManagementService, {
  BatchFormData,
  BatchLookups,
} from "../../../_services/BatchManagementService";
import "../../course-management/course-form-panel.css";

interface Props {
  show: boolean;
  onHide: () => void;
  batchId: number | null;
  lookups: BatchLookups;
  onSuccess: () => void;
}

const emptyForm: BatchFormData = {
  name: "",
  batchCode: null,
  batchShortCode: null,
  displayName: null,
  schoolId: null,
  programId: null,
  courseId: null,
  yearId: null,
  currentTermId: null,
  batchTypeId: 1,
  parentId: null,
  locationText: null,
  remarks: null,
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  maxStrength: null,
  sortOrder: null,
  activeStatus: 1,
  tagIds: [],
};

/** react-select sizing to match the .cfp form controls. */
const rsStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 40,
    borderRadius: 9,
    borderColor: "var(--border-strong)",
    fontSize: 13.5,
  }),
  menu: (base: any) => ({ ...base, zIndex: 1100 }),
};

/** Card-style section wrapper with a coloured icon header. */
const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  accent: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, accent, extra, children }) => (
  <div className="cfp-section">
    <div className="cfp-section-head">
      <span className="cfp-section-icon" style={{ background: `${accent}1a`, color: accent }}>
        {icon}
      </span>
      <span className="cfp-section-title">{title}</span>
      {extra && <span className="cfp-section-extra">{extra}</span>}
    </div>
    {children}
  </div>
);

const BatchFormPanel: React.FC<Props> = ({ show, onHide, batchId, lookups, onSuccess }) => {
  const [formData, setFormData] = useState<BatchFormData>({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = batchId !== null;

  useEffect(() => {
    if (!show) return;
    if (batchId) {
      loadBatch(batchId);
    } else {
      setFormData({ ...emptyForm });
      setError(null);
    }
  }, [batchId, show]);

  const loadBatch = async (id: number) => {
    setFetching(true);
    setError(null);
    const res = await BatchManagementService.getBatchById(id);
    if (res.success && res.data) {
      const b = res.data;
      setFormData({
        name: b.name || "",
        batchCode: b.batchCode,
        batchShortCode: b.batchShortCode,
        displayName: b.displayName,
        schoolId: b.schoolId,
        programId: b.programId,
        courseId: b.courseId,
        yearId: b.yearId,
        currentTermId: b.currentTermId,
        batchTypeId: b.batchTypeId ?? 1,
        // A batch cannot be its own parent. If the stored ParentId is a
        // self-reference (bad data), the dropdown excludes self from its
        // options and silently shows "-- None --" while retaining the stale
        // id, which then fails the backend guard on submit. Normalize to null
        // so the UI matches state and the next update clears the bad value.
        parentId: b.parentId === id ? null : b.parentId,
        locationText: b.locationText,
        remarks: b.remarks,
        startDate: b.startDate ? String(b.startDate).slice(0, 10) : null,
        endDate: b.endDate ? String(b.endDate).slice(0, 10) : null,
        startTime: b.startTime,
        endTime: b.endTime,
        maxStrength: b.maxStrength,
        sortOrder: b.sortOrder,
        activeStatus: b.activeStatus ?? 1,
        tagIds: b.tagIds ?? [],
      });
    } else {
      setError(res.message || "Failed to load batch details");
    }
    setFetching(false);
  };

  const setField = (field: keyof BatchFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const setNumberField = (field: keyof BatchFormData, raw: string) => {
    setField(field, raw === "" ? null : Number(raw));
  };

  const toggleTag = (tagId: number) => {
    setFormData((prev) => {
      const ids = prev.tagIds.includes(tagId)
        ? prev.tagIds.filter((t) => t !== tagId)
        : [...prev.tagIds, tagId];
      return { ...prev, tagIds: ids };
    });
  };

  // In program-based mode, terms are filtered by the selected program
  const termsForProgram = useMemo(() => {
    if (!lookups.isProgramBased) return [];
    if (formData.programId == null) return lookups.terms;
    return lookups.terms.filter((t) => t.programId === formData.programId);
  }, [lookups.terms, lookups.isProgramBased, formData.programId]);

  // Parents exclude self in edit mode
  const parentOptions = useMemo(() => {
    if (!isEditMode) return lookups.parents;
    return lookups.parents.filter((p) => p.id !== batchId);
  }, [lookups.parents, isEditMode, batchId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!formData.schoolId) {
      setError("BU (School) is required");
      return;
    }
    if (lookups.isProgramBased) {
      if (!formData.programId) {
        setError("Program is required");
        return;
      }
    } else {
      if (!formData.courseId) {
        setError("Course is required");
        return;
      }
    }
    if (!formData.yearId) {
      setError("Intake (Year) is required");
      return;
    }
    if (!formData.batchTypeId) {
      setError("Batch Type is required");
      return;
    }

    setLoading(true);
    try {
      const res = isEditMode
        ? await BatchManagementService.updateBatch(batchId!, formData)
        : await BatchManagementService.createBatch(formData);

      if (res.success) {
        onSuccess();
      } else {
        setError(res.message || `Failed to ${isEditMode ? "update" : "create"} batch`);
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="cfp-offcanvas">
      <Offcanvas.Header closeButton className="cfp-header">
        <div className="cfp-header-inner">
          <span className="cfp-header-badge">
            {isEditMode ? <Icon.Edit2 size={20} /> : <Icon.Plus size={22} />}
          </span>
          <div>
            <h2 className="cfp-header-title">{isEditMode ? "Edit" : "Create"} Batch</h2>
            <p className="cfp-header-sub">
              {isEditMode ? "Update the details of this batch" : "Fill in the details to add a new batch"}
            </p>
          </div>
        </div>
      </Offcanvas.Header>

      {fetching ? (
        <div className="cfp-loading">
          <Spinner role="status" animation="border" variant="primary" />
          <span>Loading batch details…</span>
        </div>
      ) : (
        <Form onSubmit={handleSubmit} className="cfp-form d-flex flex-column h-100 overflow-hidden">
          <Offcanvas.Body className="cfp-body flex-grow-1 overflow-auto">
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="cfp-alert py-2 px-3 mb-3 small">
                <Icon.AlertCircle size={15} className="me-2" />
                {error}
              </Alert>
            )}

            {/* Section: Basic Information */}
            <SectionCard icon={<Icon.Info size={16} />} title="Basic Information" accent="#6366f1">
              <div className="mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Batch name"
                  value={formData.name}
                  onChange={(e) => setField("name", e.target.value)}
                  required
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-6">
                  <Form.Label>Batch Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.batchCode ?? ""}
                    onChange={(e) => setField("batchCode", e.target.value || null)}
                  />
                </div>
                <div className="col-6">
                  <Form.Label>Short Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.batchShortCode ?? ""}
                    onChange={(e) => setField("batchShortCode", e.target.value || null)}
                  />
                </div>
              </div>

              <div>
                <Form.Label>Display Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.displayName ?? ""}
                  onChange={(e) => setField("displayName", e.target.value || null)}
                />
              </div>
            </SectionCard>

            {/* Section: Assignments */}
            <SectionCard icon={<Icon.Layers size={16} />} title="Assignments" accent="#8b5cf6">
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <Form.Label>
                    BU (School) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.schoolId ?? ""}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : null;
                      setField("schoolId", v);
                      // If the currently-selected program isn't mapped to this school, clear it
                      if (v && formData.programId && lookups.isProgramBased) {
                        const allowed = new Set(
                          (lookups.programSchools || [])
                            .filter((ps) => ps.schoolId === v)
                            .map((ps) => ps.programId)
                        );
                        if (!allowed.has(formData.programId)) {
                          setField("programId", null);
                          setField("currentTermId", null);
                          setField("yearId", null);
                        }
                      }
                    }}
                    required
                  >
                    <option value="">-- Select --</option>
                    {lookups.schools.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </Form.Select>
                </div>

                {lookups.isProgramBased ? (
                  <div className="col-6">
                    <Form.Label>
                      Program <span className="text-danger">*</span>
                    </Form.Label>
                    <ReactSelect
                      isClearable
                      isDisabled={!formData.schoolId}
                      placeholder={formData.schoolId ? "Type to search..." : "Select BU first"}
                      options={(() => {
                        if (!formData.schoolId) return [];
                        const allowed = new Set(
                          (lookups.programSchools || [])
                            .filter((ps) => ps.schoolId === formData.schoolId)
                            .map((ps) => ps.programId)
                        );
                        return lookups.programs
                          .filter((p) => allowed.has(p.id))
                          .map((p) => ({
                            value: p.id,
                            label: p.code ? `${p.name} [${p.code}]` : p.name,
                          }));
                      })()}
                      value={
                        formData.programId
                          ? (() => {
                              const p = lookups.programs.find((pr) => pr.id === formData.programId);
                              return p ? { value: p.id, label: p.code ? `${p.name} [${p.code}]` : p.name } : null;
                            })()
                          : null
                      }
                      onChange={(opt: any) => {
                        const v = opt ? opt.value : null;
                        setField("programId", v);
                        if (formData.currentTermId) {
                          const t = lookups.terms.find((tm) => tm.id === formData.currentTermId);
                          if (t && t.programId !== v) setField("currentTermId", null);
                        }
                        // If the chosen intake isn't valid for the new program, clear it
                        if (v && formData.yearId) {
                          const allowed = new Set(
                            (lookups.programYears || []).filter((py) => py.programId === v).map((py) => py.yearId)
                          );
                          if (!allowed.has(formData.yearId)) setField("yearId", null);
                        }
                      }}
                      styles={rsStyles}
                    />
                  </div>
                ) : (
                  <div className="col-6">
                    <Form.Label>
                      Course <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={formData.courseId ?? ""}
                      onChange={(e) => setField("courseId", e.target.value ? Number(e.target.value) : null)}
                      required
                    >
                      <option value="">-- Select --</option>
                      {lookups.courses.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </Form.Select>
                  </div>
                )}
              </div>

              <div className="row g-3 mb-3">
                <div className="col-6">
                  <Form.Label>
                    Intake (Year) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.yearId ?? ""}
                    onChange={(e) => setField("yearId", e.target.value ? Number(e.target.value) : null)}
                    required
                    disabled={lookups.isProgramBased && !formData.programId}
                  >
                    <option value="">
                      {lookups.isProgramBased && !formData.programId ? "Select a program first" : "-- Select --"}
                    </option>
                    {(lookups.isProgramBased && formData.programId
                      ? (() => {
                          const allowed = new Set(
                            (lookups.programYears || [])
                              .filter((py) => py.programId === formData.programId)
                              .map((py) => py.yearId)
                          );
                          return lookups.years.filter((y) => allowed.has(y.id));
                        })()
                      : lookups.years
                    ).map((y) => (
                      <option key={y.id} value={y.id}>{y.name}</option>
                    ))}
                  </Form.Select>
                </div>

                {lookups.isProgramBased && (
                  <div className="col-6">
                    <Form.Label>Current Term</Form.Label>
                    <Form.Select
                      value={formData.currentTermId ?? ""}
                      onChange={(e) => setField("currentTermId", e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">-- None --</option>
                      {termsForProgram.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Form.Select>
                  </div>
                )}
              </div>

              <div className="row g-3">
                <div className="col-6">
                  <Form.Label>Is Child Of (Parent Batch)</Form.Label>
                  <Form.Select
                    value={formData.parentId ?? ""}
                    onChange={(e) => setField("parentId", e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">-- None --</option>
                    {parentOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-6">
                  <Form.Label>
                    Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.batchTypeId ?? ""}
                    onChange={(e) => setField("batchTypeId", e.target.value ? Number(e.target.value) : null)}
                    required
                  >
                    {lookups.batchTypes.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            </SectionCard>

            {/* Section: Tags */}
            {lookups.tags.length > 0 && (
              <SectionCard
                icon={<Icon.Star size={16} />}
                title="Tags"
                accent="#f59e0b"
                extra={`${formData.tagIds.length} selected`}
              >
                <div className="cfp-checklist">
                  {lookups.tags.map((t) => (
                    <Form.Check
                      key={t.id}
                      type="checkbox"
                      id={`tag-${t.id}`}
                      label={t.name}
                      checked={formData.tagIds.includes(t.id)}
                      onChange={() => toggleTag(t.id)}
                    />
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Section: Schedule & Capacity */}
            <SectionCard icon={<Icon.Calendar size={16} />} title="Schedule & Capacity" accent="#10b981">
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.startDate ?? ""}
                    onChange={(e) => setField("startDate", e.target.value || null)}
                  />
                </div>
                <div className="col-6">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.endDate ?? ""}
                    onChange={(e) => setField("endDate", e.target.value || null)}
                  />
                </div>
              </div>

              <div className="row g-3">
                <div className="col-4">
                  <Form.Label>Max Strength</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.maxStrength ?? ""}
                    onChange={(e) => setNumberField("maxStrength", e.target.value)}
                  />
                </div>
                <div className="col-4">
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.sortOrder ?? ""}
                    onChange={(e) => setNumberField("sortOrder", e.target.value)}
                  />
                </div>
                {isEditMode && (
                  <div className="col-4">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={formData.activeStatus ?? 1}
                      onChange={(e) => setField("activeStatus", Number(e.target.value))}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Form.Select>
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Section: Additional Info */}
            <SectionCard icon={<Icon.MapPin size={16} />} title="Additional Info" accent="#64748b">
              <div className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.locationText ?? ""}
                  onChange={(e) => setField("locationText", e.target.value || null)}
                />
              </div>
              <div>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.remarks ?? ""}
                  onChange={(e) => setField("remarks", e.target.value || null)}
                  style={{ resize: "vertical" }}
                />
              </div>
            </SectionCard>
          </Offcanvas.Body>

          {/* Footer */}
          <div className="cfp-footer">
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <Spinner role="status" animation="border" size="sm" />
              ) : (
                <>
                  <Icon.Save size={15} className="me-1" />
                  {isEditMode ? "Update" : "Create"}
                </>
              )}
            </Button>
          </div>
        </Form>
      )}
    </Offcanvas>
  );
};

export default BatchFormPanel;
